import AppStoreButtons from "components/downloadappcta/appstorebuttons/AppStoreButtons";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { t } from "i18n/index";
import { useMarketingLang } from "i18n/useMarketingLang";
import { getHomeTranslations } from "../marketing/i18n/pages/home";

const StarIcon = ({ size = 20 }: { size?: number }) => (
	<svg width={size} height={size} viewBox="0 0 20 20" fill="#f5a623">
		<path d="M10 2l2.36 4.78 5.27.77-3.82 3.72.9 5.26L10 13.77l-4.71 2.76.9-5.26L2.37 7.55l5.27-.77L10 2z" />
	</svg>
);

const Stars = ({ size }: { size?: number }) => (
	<div className="rv-card__stars">
		{[0, 1, 2, 3, 4].map((i) => (
			<StarIcon key={i} size={size} />
		))}
	</div>
);

type ReviewItem = {
	title: string;
	text: string;
	avatarColor: string;
	/** Flag + country, e.g. "🇬🇧 UK". The only thing said about who left the review. */
	location: string;
};

const ROW1: ReviewItem[] = [
	{
		title: "“Been waiting for this”",
		text: "“If ever there was an app I would have built myself if I had more brains and motivation, this would be it. Only been using it for 5 minutes and managed to replace my (albeit amazing) 10 tab excel spreadsheet covering our upcoming itinerary, budget, bookings, spending… into one place. I’ve immediately upgraded to paid as I think the monthly subscription is incredibly fair for what you get.”",
		avatarColor: "#13c892",
		location: "🇬🇧 UK",
	},
	{
		title: "“The perfect app!”",
		text: "“This is my first time ever leaving an app review, but this is the BEST travel planner made. I tell every single person about this, every feature and the interface is just perfect. The fact that an app of this calibre is free is honestly really generous.”",
		avatarColor: "#e84d7e",
		location: "🇦🇺 Australia",
	},
	{
		title: "“Great all-around app”",
		text: "“I think some people just don’t understand the full potential of this app. There is so much you can add and do and accomplish with this. A fantastic travel go-to crutch!”",
		avatarColor: "#7c3aed",
		location: "🇺🇸 USA",
	},
];

const ROW2: ReviewItem[] = [
	{
		title: "“Everything you’ve ever wanted for travel”",
		text: "“I used to always use excel to plan my trips, struggled with keeping all my notes together and not forgetting the dates to book different accommodations when going to various booking websites, this now is all in one place! Super easy to use and you can just copy trips, drag and drop, save your spots, get inspired and plan all in one app.”",
		avatarColor: "#f5a623",
		location: "🇳🇱 Netherlands",
	},
	{
		title: "“I love this app!”",
		text: "“We’re planning our very first family holiday and Stippl made the process so much more exciting. The multi stop trip is fully tracked, hotels, and ESPECIALLY restaurant recommendations through the app, that was so helpful. We’ve shortlisted a whole bunch of places to go to thanks to Stippl. Thank you for making our trip so much better.”",
		avatarColor: "#0f3e4a",
		location: "🇮🇳 India",
	},
];

// Leading regional-indicator pair of `location` ("🇬🇧 UK" -> "🇬🇧"), for the avatar chip.
const flagPart = (location: string) => (location.match(/^\p{Regional_Indicator}{2}/u) || [""])[0];
const namePart = (location: string) => location.replace(/^\p{Regional_Indicator}{2}\s*/u, "");

/**
 * This variant is live on `/itinerary-planner`, and until 123yb6937h6 it rendered "Ella
 * McFarlane" — a reviewer's full real name — plus two cards' initials, next to two others already
 * attributed to "App Store". Same rule as the homepage carousel: credit the source, not the
 * person. The avatar keeps its coloured chip and carries the country flag instead of initials
 * derived from a name we should not be publishing.
 */
const ReviewCard = ({ item }: { item: ReviewItem }) => (
	<div className="rv-card">
		<Stars />
		<h5 className="rv-card__title">{item.title}</h5>
		<p className="rv-card__text">{item.text}</p>
		<div className="rv-card__author">
			<div className="rv-card__avatar" style={{ background: item.avatarColor }} aria-hidden="true">
				{flagPart(item.location)}
			</div>
			<div className="rv-card__info">
				<span className="rv-card__name">App Store</span>
				<span className="rv-card__location">{namePart(item.location)}</span>
			</div>
		</div>
	</div>
);

// ── Homepage-only variant — mirrors the redesign's HomeReviewsSection exactly ──

// `count` defaults to 5 for the ticker pills, which carry no per-item rating. A review card always
// passes its own, so a four-star review can never be drawn as five (123yb6937h6).
const HomeStars = ({ size = 15, count = 5 }: { size?: number; count?: number }) => (
	<div className="home-rv-stars">
		{Array.from({ length: count }, (_, i) => i).map((i) => (
			<svg key={i} width={size} height={size} viewBox="0 0 18 18" fill="#ffc14a">
				<path d="M9 1.5l2.06 4.17 4.61.67-3.34 3.25.79 4.58L9 11.77l-4.12 2.4.79-4.58L2.33 6.34l4.61-.67z" />
			</svg>
		))}
	</div>
);

type HomeReviewItem = {
	title: string;
	body: string;
	/**
	 * An opaque per-reviewer key, NOT a name or a handle.
	 *
	 * The rule is that no reviewer identity reaches a Stippl surface, and "we don't render it" is
	 * not the same as "we don't publish it": anything in this array ships inside the JS bundle and
	 * is readable by anyone who opens the sources tab. So the names are not here at all.
	 *
	 * A key rather than nothing, because the defect this block exists to fix was ten cards from
	 * five people — and that is only assertable if two reviews by the same person are still
	 * distinguishable from two by different people. The key ↔ reviewer mapping lives on ClickUp
	 * 123yb6937h6, which is where an audit trail belongs, not in a public bundle.
	 */
	reviewerKey: string;
	/** ISO-3166 alpha-2 of the storefront the review was left on, rendered as a flag. */
	country: string;
	/** Month the review was posted, as YYYY-MM. Rendered as "Aug 2026". */
	posted: string;
	rating: 1 | 2 | 3 | 4 | 5;
};

/**
 * Ten reviews, ten different people, every word as the reviewer wrote it (123yb6937h6).
 *
 * What was here before: ten cards attributed to **five** App Store handles — one of them three
 * times, another three times, a third twice — and two of that first reviewer's entries were
 * visibly the same review written twice ("Replaced my 10-tab Excel spreadsheet in 5 minutes…" and "Only been using it for 5
 * minutes and managed to replace my 10 tab excel spreadsheet…"). Worse than the repetition: the
 * shorter one was a *condensed rewrite* of the real review, so it was a quote nobody had actually
 * written, attributed to a real person. Anyone who read two cards concluded the reviews were
 * fabricated. They are not — 8,759 App Store ratings averaging 4.67 across 38 storefronts, checked
 * the day this shipped — but the block made them look that way, which is the whole problem with
 * social proof nobody has verified.
 *
 * These ten came from App Store Connect on 2026-09-15. Selection rule: five stars, one review per
 * author, body used **in full**. Nothing is trimmed to hide a caveat — a review that mixes praise
 * with a complaint is left out entirely rather than cut down to its first sentence, because a
 * trimmed quote is the same credibility problem in a subtler form.
 *
 * To add one: a real review, complete, from an author not already here. The test in
 * `__tests__/TestimonialsSection.test.tsx` fails on a repeated author.
 */
const HOME_COL_1: HomeReviewItem[] = [
	{
		title: "Planned Grad Trip + Amazing Customer Support",
		body: "We used Stippl to plan and organize everything, including expenses and settle balances, for our whole grad trip of five guys over the course of 20 days. It was extremely user intuitive and really helped us keep everything in one centralized place and we referenced it a million times during the trip. Plus it was easy to settle balances and account for things. Very customizable. I would definitely recommend.",
		reviewerKey: "r1",
		country: "CA",
		posted: "2026-05",
		rating: 5,
	},
	{
		title: "I never pay for apps… well worth the money.",
		body: "This app is legit a one stop shop. You can log every part of your trip in the app to ensure all of the pieces of your trip are linked together. It’s so organized we were able to plan our Europe trip over the course of a couple of weeks stress free easily being able to pick up where we left off.",
		reviewerKey: "r2",
		country: "US",
		posted: "2026-03",
		rating: 5,
	},
	{
		title: "Great app for overview of trip",
		body: "I have the free version and find that it is great to list everything you have planned. I found the customer service very responsive when I came across a difficulty in trying to add something. It is simple to use - I will look forward to using the journal aspect.",
		reviewerKey: "r3",
		country: "AU",
		posted: "2026-08",
		rating: 5,
	},
];

const HOME_COL_2: HomeReviewItem[] = [
	{
		title: "Literally the best app ever",
		body: "So easy to use, super helpful, and best planner ever",
		reviewerKey: "r4",
		country: "GB",
		posted: "2026-08",
		rating: 5,
	},
	{
		title: "Positive experience with Stippl",
		body: "Great customer service offering quick support. Internet service works when travelling outside of EU and the price is affordable. Recommended to use 😊",
		reviewerKey: "r5",
		country: "NO",
		posted: "2026-08",
		rating: 5,
	},
];

const HOME_COL_3: HomeReviewItem[] = [
	{
		title: "Really Useful App",
		body: "It is super useful specially since it enables everything to be in one place, bookings, budget, activities and it’s free! Also their customer support is amazing and helpfull!",
		reviewerKey: "r6",
		country: "PT",
		posted: "2026-06",
		rating: 5,
	},
	{
		title: "Love it",
		body: "I’ve used this to plan my south east Asia trip and it’s been so helpful on keeping track of where I’m meant to be going and what I’ve already done this app is definitely a essential when it comes to travelling the world",
		reviewerKey: "r7",
		country: "GB",
		posted: "2026-04",
		rating: 5,
	},
];

const HOME_COL_4: HomeReviewItem[] = [
	{
		title: "Best app for planning",
		body: "Very user friendly easy to change and move things best app I’ve ever used to organise travel",
		reviewerKey: "r8",
		country: "AU",
		posted: "2026-04",
		rating: 5,
	},
	{
		title: "Best way to plan our trip!",
		body: "Best way to plan our trip!",
		reviewerKey: "r9",
		country: "NL",
		posted: "2026-08",
		rating: 5,
	},
	{
		title: "So good",
		body: "This app is so good for planning. Thank you!",
		reviewerKey: "r10",
		country: "IS",
		posted: "2025-12",
		rating: 5,
	},
];

const HOME_COLUMNS = [HOME_COL_1, HOME_COL_2, HOME_COL_3, HOME_COL_4];

/**
 * Exported for the tests only. No reviewer name or handle exists here to assert against — see
 * `reviewerKey` — so the one-review-per-person rule this block exists to enforce is asserted on
 * that opaque key instead. Removing the names must not quietly delete the guarantee with them.
 */
export const HOME_REVIEWS = HOME_COLUMNS.flat();

// Tablet/mobile show one stacked column capped at 4 cards (one per desktop column) instead of the
// 4-up grid, so there's no horizontal scroll and no ~14-card wall of vertical scroll (86cb93kaw).
const HOME_MOBILE_ITEMS: HomeReviewItem[] = HOME_COLUMNS.map((col) => col[0]);

// A country code to its flag emoji: each letter maps to its regional-indicator symbol, which is a
// fixed offset away in the code-point table. No image request, no CDN, and it inherits the text
// colour and size, so it needs no styling of its own.
const flagOf = (country: string) =>
	country
		.toUpperCase()
		.replace(/[^A-Z]/g, "")
		.split("")
		.map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
		.join("");

// "2026-05" -> "May 2026". Built from the parts rather than `new Date("2026-05")`, which is parsed
// as UTC midnight and can render the previous month for anyone west of Greenwich.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const postedLabel = (posted: string) => {
	const [year, month] = posted.split("-");
	const name = MONTHS[Number(month) - 1];
	return name ? `${name} ${year}` : year;
};

/**
 * The byline attributes the review to its SOURCE, not to a person — country, month, store.
 *
 * No reviewer identity is in this file at all — not rendered, and not in the data either, because
 * anything in that array ships inside the JS bundle whether it is rendered or not. This comment
 * deliberately does not quote the ones that used to be here; the test below greps this file for
 * them, and an example in a comment explaining why we removed them is still the thing we removed.
 *
 * Showing them meant one of two bad outcomes, and the first cut shipped both at once. Reviewers who
 * use a handle produced strings that read as invented precisely because they look made up — the
 * opposite of what this block is for. Reviewers who use their real name produced a full name
 * republished on a marketing homepage, a step beyond where that person chose to leave it.
 *
 * Shortening a handle was not a way out either: there is no honest way to turn a random alphanumeric
 * handle into a first name, and inventing one would break the single property this block depends
 * on — every word of it is real. An anonymous review with checkable provenance is more credible
 * than a named one where the name had to be shaped.
 *
 * This matches what the non-homepage variant above already does — two of its five cards are
 * attributed to "App Store" rather than a person, for the same reason.
 *
 * The flag and month still vary per card, so ten cards still read as ten different travellers.
 * The uniqueness guarantee survives the names' removal: it is asserted on the opaque
 * `reviewerKey` via the `HOME_REVIEWS` export below, so "ten cards, five people" is still a test
 * failure rather than something you would have to notice by eye.
 */
const HomeReviewCard = ({ item }: { item: HomeReviewItem }) => (
	<div className="home-rv-card">
		<HomeStars count={item.rating} />
		<div className="home-rv-card__body">
			<p className="home-rv-card__title">{item.title}</p>
			<p className="home-rv-card__text">{item.body}</p>
		</div>
		<p className="home-rv-card__username">
			<span aria-hidden="true">{flagOf(item.country)}</span> {postedLabel(item.posted)} · App Store
		</p>
	</div>
);

/**
 * The ticker pills carry real review titles, from the same ten reviews as the cards below
 * (123yb6937h6).
 *
 * They used to pair ten Unsplash stock portraits with ten *paraphrased* quotes — "Replaced my
 * Excel in 5 min" is a condensed version of a real review, not anything anyone wrote. Photographs
 * of strangers presented as customers is a worse claim than a duplicated username, and it is the
 * part a reader can check with a reverse image search. The pills now show a title and stars and
 * nothing else; there is no avatar because we do not have reviewers' photographs and should not
 * imply we do.
 */
const HOME_TICKER_ITEMS = HOME_COLUMNS.flat().map((r) => r.title);

const HomeReviewTicker = () => (
	<div className="home-rv-ticker">
		<div className="home-rv-ticker__track">
			{[...HOME_TICKER_ITEMS, ...HOME_TICKER_ITEMS].map((text, i) => (
				<div className="home-rv-ticker__pill" key={i}>
					<HomeStars size={10} />
					<span className="home-rv-ticker__text">{text}</span>
				</div>
			))}
		</div>
	</div>
);

const HOME_S_PHOTO = "https://images.unsplash.com/photo-1643029891412-92f9a81a8c16?fit=crop&auto=format&q=80&w=900&h=1200";
const HOME_S_CLIP_PATH =
	"M73.1074 416.95C62.7264 416.95 54.4857 425.243 54.687 435.475C54.8958 445.7 63.4721 454 73.8457 454C84.2268 454 92.4675 445.7 92.2662 435.475C92.0574 425.243 83.481 416.95 73.1074 416.95ZM274.643 140.241C320.001 128.182 338.071 101.327 338.108 70.3779C338.16 28.3988 304.146 0 248.385 0C160.556 0 0 94.5184 0 181.258C0 249.295 88.4404 271.369 148.393 285.42C208.352 299.462 254.694 312.222 254.694 328.808C254.694 345.387 201.812 382.757 116.638 409.784C107.51 412.819 102.177 420.977 104.713 428.003C107.256 435.028 116.504 437.519 125.841 435.222C161.317 426.474 328.734 388.201 328.734 312.841C328.734 237.482 148.333 206.659 148.333 192.952C148.333 178.879 237.937 149.996 274.643 140.241Z";

const HomeReviewsDecoration = () => (
	<div className="home-rv-decorative" aria-hidden="true">
		<svg width="100%" height="100%" viewBox="0 0 338 454" fill="none" xmlns="http://www.w3.org/2000/svg">
			<defs>
				<clipPath id="home-rv-s-clip">
					<path d={HOME_S_CLIP_PATH} />
				</clipPath>
			</defs>
			<image href={HOME_S_PHOTO} x="0" y="0" width="338" height="454" preserveAspectRatio="xMidYMid slice" clipPath="url(#home-rv-s-clip)" />
		</svg>
	</div>
);

const HomeReviewsCta = ({ translations }: { translations: Record<string, string> }) => {
	// 123yb691qz1: opens the signup modal in place instead of navigating to /register;
	// `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store instead.
	const gate = usePlanGate("homepage_testimonials_cta", "store");

	return (
		<div className="home-rv-cta">
			<p className="home-rv-cta__eyebrow">{t(translations, "home.testimonials.eyebrow", "Loved by millions of travelers worldwide")}</p>
			<h2 className="home-rv-cta__heading">{t(translations, "home.testimonials.heading", "Your next trip starts here.")}</h2>

			<div className="home-rv-cta__actions">
				<a
					href="/register"
					className="home-rv-cta__btn"
					onClick={(e) => {
						e.preventDefault();
						gate.requestPlan();
					}}
				>
					{t(translations, "home.testimonials.ctaText", "Start planning, it's free")}
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
						<path d="M5 12h14M12 5l7 7-7 7" />
					</svg>
				</a>
				<AppStoreButtons location="marketing_site" />
			</div>
			<PlanGateModal
				gate={gate}
				title={t(translations, "home.auth.startPlanning", "Create a free account to start planning")}
				loginTitle={t(translations, "home.auth.login", "Log in to plan with AI")}
			/>

			<div className="home-rv-cta__ratings">
				<div className="home-rv-cta__rating">
					<HomeStars size={11} />
					<span>
						<strong>4.7</strong> App Store
					</span>
				</div>
				<div className="home-rv-cta__divider" />
				<div className="home-rv-cta__rating">
					<HomeStars size={11} />
					<span>
						<strong>4.4</strong> Google Play
					</span>
				</div>
			</div>
		</div>
	);
};

type TestimonialsSectionProps = {
	/** "homepage" mirrors the redesign's HomeReviewsSection exactly — staggered columns, S-shape photo cutout, avatar ticker and CTA. Other consumers keep the default look. */
	variant?: "default" | "homepage";
};

const TestimonialsSection = ({ variant = "default" }: TestimonialsSectionProps) => {
	const lang = useMarketingLang();
	const translations = getHomeTranslations(lang);

	if (variant === "homepage") {
		return (
			<div className="testimonials-section testimonials-section--homepage">
				<HomeReviewsDecoration />
				<div className="home-rv-container">
					<div className="home-rv-columns">
						{HOME_COLUMNS.map((col, ci) => (
							<div className={`home-rv-column home-rv-column--${ci + 1}`} key={ci}>
								{col.map((item, i) => (
									<HomeReviewCard key={i} item={item} />
								))}
							</div>
						))}
					</div>
					<div className="home-rv-columns-mobile">
						{HOME_MOBILE_ITEMS.map((item, i) => (
							<HomeReviewCard key={i} item={item} />
						))}
					</div>

					<HomeReviewTicker />
					<HomeReviewsCta translations={translations} />
				</div>
			</div>
		);
	}

	return (
		<div className="testimonials-section">
			<div className="container__inner">
				<div className="rv-header">
					<span className="hr-pill">{t(translations, "home.testimonials.pill", "Real travellers, real trips")}</span>
					<h2>
						{t(translations, "home.testimonials.defaultHeadingLine1", "Over 2 million trips planned")}
						<br />
						{t(translations, "home.testimonials.defaultHeadingLine2", "by travellers like you")}
					</h2>
				</div>
				<div className="rv-grid rv-grid--3">
					{ROW1.map((item, i) => (
						<ReviewCard key={i} item={item} />
					))}
				</div>
				<div className="rv-grid rv-grid--2">
					{ROW2.map((item, i) => (
						<ReviewCard key={i} item={item} />
					))}
				</div>
			</div>
		</div>
	);
};

export default TestimonialsSection;
